import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import apiClient from '../../../services/api';
import {
  establishmentDateFromForm,
  fetchFieldCropInformation,
  fieldCropHasDisplayableInfo,
  formValuesFromFieldCrop,
} from '../../../config/digisakaApi';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import { getAnalysisPanelTheme } from '../theme/themeStyles';
import { onEndDateChange, onStartDateChange } from '../utils/dateRangeInputs';
import type { FarmBriefLang } from '../utils/downloadFarmBrief';

const SEASON_SAVE_FIRST_MSG = 'Please save crop information for this field first.';

type ReportKind = 'brief' | 'fieldHealth';
type RangeKey = '30' | 'season' | 'custom';
type Delivery = 'dl' | 'em';
type Frequency = 'weekly' | 'monthly';

type Subscription = {
  id: string;
  report_type: 'brief' | 'field_health';
  frequency: Frequency | 'daily';
  enabled: boolean;
  recipient_email: string;
};

const PRESETS: { k: string; n: string; d: string; m: string[]; icon: string; badge?: boolean }[] = [
  {
    k: 'rec',
    n: 'Recommended',
    d: 'Crop greenness, including dense and bare-soil fields',
    m: ['ndvi', 'evi', 'msavi2'],
    icon: 'ri-leaf-line',
    badge: true,
  },
  { k: 'green', n: 'Greenness only', d: 'The simplest view of crop growth', m: ['ndvi'], icon: 'ri-plant-line' },
  { k: 'water', n: 'Water and dryness', d: 'How much water the crop and soil hold', m: ['ndmi', 'ndii', 'ndwi'], icon: 'ri-drop-line' },
  { k: 'radar', n: 'Cloudy-day view', d: 'Radar readings that work through clouds', m: ['rvi', 'ndpi'], icon: 'ri-cloud-line' },
];

const GROUPS: [string, [string, string][]][] = [
  [
    'Greenness',
    [
      ['ndvi', 'Crop greenness'],
      ['evi', 'Greenness, dense crop'],
      ['msavi2', 'Greenness on bare soil'],
      ['savi', 'Greenness, soil adjusted'],
      ['vari', 'Visible greenness'],
      ['arvi', 'Greenness through haze'],
      ['rgr', 'Red-to-green ratio'],
    ],
  ],
  [
    'Stress and aging',
    [
      ['sipi', 'Leaf pigment stress'],
      ['psri', 'Crop aging'],
      ['rendvi', 'Red-edge greenness'],
      ['ireci', 'Chlorophyll'],
      ['s2rep', 'Red-edge position'],
    ],
  ],
  [
    'Water',
    [
      ['ndii', 'Canopy water'],
      ['ndmi', 'Crop moisture'],
      ['ndwi', 'Surface water'],
    ],
  ],
  [
    'Radar (works through cloud)',
    [
      ['rvi', 'Radar vegetation'],
      ['ndpi', 'Radar surface'],
    ],
  ],
  ['Other', [['nbr', 'Burn signal']]],
];

function ymd(d: Date): string {
  const z = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

function pretty(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function last30(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { start: ymd(start), end: ymd(end) };
}

function presetOf(selected: string[]): string {
  const set = new Set(selected.map((v) => v.toLowerCase()));
  const hit = PRESETS.find((p) => p.m.length === set.size && p.m.every((code) => set.has(code)));
  return hit?.k ?? 'custom';
}

function statusFromError(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
}

export default function GenerateReportPanel({
  isDark,
  farmName,
  farmId,
  reportKind,
  onReportKind,
  briefLang,
  onBriefLang,
  selectedIndices,
  onSelectedIndices,
  isGenerating,
  onDownload,
  onClose,
}: {
  isDark: boolean;
  farmName?: string | null;
  farmId?: string | number | null;
  reportKind: ReportKind;
  onReportKind: (kind: ReportKind) => void;
  briefLang: FarmBriefLang;
  onBriefLang: (lang: FarmBriefLang) => void;
  selectedIndices: string[];
  onSelectedIndices: (codes: string[]) => void;
  isGenerating: boolean;
  onDownload: (dates: { start: string; end: string }) => void;
  onClose?: () => void;
}) {
  const { user } = useAuth();
  const id = String(farmId ?? '').trim();
  const canSchedule = /^\d+$/.test(id);
  const health = reportKind === 'fieldHealth';
  const preset = presetOf(selectedIndices);

  const [range, setRange] = useState<RangeKey>('30');
  const [custom, setCustom] = useState(last30);
  const [seasonStart, setSeasonStart] = useState<string | null>(null);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [seasonError, setSeasonError] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<Delivery>('dl');
  const [query, setQuery] = useState('');
  const [measuresOpen, setMeasuresOpen] = useState(false);

  const [frequency, setFrequency] = useState<Frequency>('weekly');
  const [email, setEmail] = useState(user?.email || '');
  const [enabled, setEnabled] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'send' | 'remove' | null>(null);

  const ap = getAnalysisPanelTheme(isDark);
  const selectedCard = isDark
    ? 'border-[#34d399]/40 bg-[rgba(52,211,153,.12)]'
    : 'border-[#00854080] bg-[#00854014]';
  const chipOn = `${ap.segmentActive} report-on-fill border border-transparent`;
  const chipOff = `border ${ap.tabInactive}`;
  const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 ' +
    (isDark ? 'focus-visible:ring-[#34d399]/70' : 'focus-visible:ring-[#008540CC]');

  useEffect(() => {
    if (!email && user?.email) setEmail(user.email);
  }, [email, user?.email]);

  useEffect(() => {
    setSeasonStart(null);
    setSeasonError(null);
    setSeasonLoading(false);
    setRange((prev) => (prev === 'season' ? '30' : prev));
  }, [id]);

  const emailType = health ? 'field_health' : 'brief';

  useEffect(() => {
    if (!canSchedule) return;
    let cancelled = false;
    apiClient
      .get<Subscription[]>('/farm-report-subscriptions/', { params: { farm_id: id } })
      .then((res) => {
        if (cancelled) return;
        const match = (res.data || []).find((row) => row.report_type === emailType);
        if (!match) {
          setSubscriptionId(null);
          setEnabled(true);
          return;
        }
        setSubscriptionId(match.id);
        setFrequency(match.frequency === 'monthly' ? 'monthly' : 'weekly');
        setEmail(match.recipient_email || user?.email || '');
        setEnabled(match.enabled);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canSchedule, id, emailType, user?.email]);

  const selectSeason = useCallback(async () => {
    if (!canSchedule) {
      setSeasonError(SEASON_SAVE_FIRST_MSG);
      setSeasonStart(null);
      setRange('30');
      return;
    }
    setSeasonLoading(true);
    setSeasonError(null);
    try {
      const info = await fetchFieldCropInformation(id);
      if (!fieldCropHasDisplayableInfo(info)) {
        setSeasonError(SEASON_SAVE_FIRST_MSG);
        setSeasonStart(null);
        setRange('30');
        return;
      }
      const form = formValuesFromFieldCrop(info);
      const startRaw = establishmentDateFromForm(form);
      const start = startRaw && /^\d{4}-\d{2}-\d{2}/.test(startRaw) ? startRaw.slice(0, 10) : null;
      if (!start) {
        setSeasonError(SEASON_SAVE_FIRST_MSG);
        setSeasonStart(null);
        setRange('30');
        return;
      }
      setSeasonStart(start);
      setRange('season');
    } catch {
      setSeasonError('Could not load crop information for this field. Try again.');
      setSeasonStart(null);
      setRange('30');
    } finally {
      setSeasonLoading(false);
    }
  }, [canSchedule, id]);

  const dates = useMemo(() => {
    if (range === 'custom') return custom;
    if (range === 'season' && seasonStart) {
      const end = ymd(new Date());
      return seasonStart <= end ? { start: seasonStart, end } : { start: end, end: seasonStart };
    }
    return last30();
  }, [range, custom, seasonStart]);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const needsMeasure = health && selectedIndices.length === 0;
  const needsSeasonData = health && range === 'season' && !seasonStart;
  const freqLabel = frequency === 'monthly' ? '1st of the month' : 'Every Monday';
  const langLabel = briefLang === 'fil' ? 'Filipino' : 'English';
  const rangeLabel = range === '30' ? 'last 30 days' : range === 'season' ? 'this season' : 'custom dates';
  const summary = health
    ? `Field health · ${selectedIndices.length} measure${selectedIndices.length === 1 ? '' : 's'} · ${rangeLabel} · ${langLabel}`
    : `Regular farm brief · ${langLabel}`;

  const q = query.trim().toLowerCase();
  const filtered = GROUPS.map(([group, items]) => [
    group,
    items.filter(([code, name]) => `${code} ${name}`.toLowerCase().includes(q)),
  ] as [string, [string, string][]]).filter(([, items]) => items.length > 0);

  const pickPreset = (codes: string[]) => onSelectedIndices(codes);
  const toggleMeasure = (code: string, on: boolean) => {
    const next = new Set(selectedIndices.map((v) => v.toLowerCase()));
    if (on) next.add(code);
    else next.delete(code);
    onSelectedIndices([...next]);
  };

  const save = async () => {
    if (!canSchedule || !emailOk) return;
    setBusy('save');
    setStatus(null);
    try {
      const res = await apiClient.put<Subscription>('/farm-report-subscriptions/', {
        farm_id: id,
        farm_name: farmName || null,
        report_type: emailType,
        frequency,
        enabled,
        recipient_email: email.trim(),
      });
      setSubscriptionId(res.data.id);
      setFrequency(res.data.frequency === 'monthly' ? 'monthly' : 'weekly');
      setStatus(enabled ? 'Schedule saved.' : 'Schedule paused.');
    } catch (error) {
      setStatus(statusFromError(error, 'Could not save the email schedule.'));
    } finally {
      setBusy(null);
    }
  };

  const sendNow = async () => {
    if (!canSchedule || !emailOk) return;
    setBusy('send');
    setStatus(null);
    try {
      const res = await apiClient.post<{ message?: string }>('/farm-report-subscriptions/send-now/', {
        farm_id: id,
        farm_name: farmName || null,
        report_type: emailType,
        recipient_email: email.trim(),
      });
      setStatus(res.data?.message || 'Test email sent.');
    } catch (error) {
      setStatus(statusFromError(error, 'Could not send the test email.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!subscriptionId) return;
    setBusy('remove');
    try {
      await apiClient.delete(`/farm-report-subscriptions/${subscriptionId}/`);
      setSubscriptionId(null);
      setStatus('Email schedule removed.');
    } catch (error) {
      setStatus(statusFromError(error, 'Could not remove the email schedule.'));
    } finally {
      setBusy(null);
    }
  };

  const step = (n: number, label: string) => (
    <div className={`mb-2 mt-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide ${ap.muted}`}>
      <b className={`report-on-fill inline-grid h-5 w-5 place-items-center rounded-full text-[11px] font-semibold ${ap.primaryBtn}`}>{n}</b>
      {label}
    </div>
  );

  const radio = (on: boolean, title: ReactNode, desc: string, onClick: () => void, icon?: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onClick}
      className={`mb-2 flex min-h-11 w-full items-start gap-3 rounded-[10px] border px-3 py-3 text-left ${focus} ${
        on ? selectedCard : ap.cardSolid
      }`}
    >
      {icon && (
        <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-base ${ap.iconWrapVeg}`}>
          <i className={icon} aria-hidden />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <b className={`block text-sm font-medium ${ap.heading}`}>{title}</b>
        <span className={`mt-0.5 block text-xs leading-snug ${ap.muted}`}>{desc}</span>
      </span>
      <span
        className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border text-[12px] ${
          on ? `report-on-fill border-transparent ${ap.primaryBtn}` : isDark ? 'border-white/15' : 'border-[#e5e7eb]'
        }`}
        aria-hidden
      >
        {on ? <i className="ri-check-line text-xs" /> : null}
      </span>
    </button>
  );

  return (
    <div className={`flex h-full min-h-0 flex-1 flex-col text-sm leading-snug ${ap.heading}`}>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2 pt-3">
        <div className="mb-0.5 flex items-center justify-between">
          <h4 className={`m-0 text-sm font-medium ${ap.heading}`}>Generate report</h4>
          {onClose && (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className={`grid h-9 w-9 place-items-center ${ap.headerActionHover} ${focus}`}
            >
              <i className="ri-close-line text-xl" aria-hidden />
            </button>
          )}
        </div>
        <p className={`mb-1 text-xs ${ap.muted}`}>
          {farmName || 'This farm'}
          {id ? ` · Field ID ${id}` : ''}
        </p>

        {step(1, 'What do you need?')}
        <div role="radiogroup" aria-label="Report type">
          {radio(
            reportKind === 'brief',
            'Regular farm brief',
            'A short PDF of this farm today: weather, how the crop looks, dryness, pests, and what to do in the next few days.',
            () => onReportKind('brief'),
            'ri-file-text-line'
          )}
          {radio(
            health,
            'Field health over time',
            'Pick dates. See how green and how wet the crop was, plus weather, and whether the field got better or worse.',
            () => onReportKind('fieldHealth'),
            'ri-line-chart-line'
          )}
        </div>

        {health && (
          <>
            {step(2, 'What to measure')}
            <div role="radiogroup" aria-label="What to measure">
              {PRESETS.map((item) =>
                radio(
                  preset === item.k,
                  <span>
                    {item.n}
                    {item.badge && (
                      <span className={`text-[11px] font-medium ${ap.accentText}`}> · best for most farms</span>
                    )}
                  </span>,
                  item.d,
                  () => pickPreset(item.m),
                  item.icon
                )
              )}
            </div>
            <details
              className={`mt-2 rounded-[10px] border px-3 ${ap.cardSolid}`}
              open={measuresOpen}
              onToggle={(e) => setMeasuresOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
              <summary className={`flex min-h-11 cursor-pointer list-none items-center justify-between text-sm font-medium ${focus}`}>
                <span>Pick measures yourself</span>
                <span className={`text-xs font-medium ${ap.muted}`}>{selectedIndices.length} selected</span>
              </summary>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search, e.g. water or NDVI"
                aria-label="Search measures"
                className={`mb-1 mt-1 ${ap.input} ${focus}`}
              />
              {filtered.length === 0 ? (
                <p className={`text-xs ${ap.muted}`}>No match.</p>
              ) : (
                filtered.map(([group, items]) => (
                  <div key={group}>
                    <div className={`mb-0.5 mt-3 text-[11px] font-semibold uppercase tracking-wide ${ap.muted}`}>{group}</div>
                    {items.map(([code, name]) => (
                      <label key={code} className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedIndices.map((v) => v.toLowerCase()).includes(code)}
                          onChange={(e) => toggleMeasure(code, e.target.checked)}
                          className={`h-4 w-4 shrink-0 ${isDark ? 'accent-[#34d399]' : 'accent-[#008540]'}`}
                        />
                        <span>{name}</span>
                        <small className={`ml-auto rounded-md border px-1.5 py-px text-[11px] ${ap.cardSolid} ${ap.muted}`}>
                          {code.toUpperCase()}
                        </small>
                      </label>
                    ))}
                  </div>
                ))
              )}
            </details>

            {step(3, 'Dates')}
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['30', 'Last 30 days'],
                  ['season', 'This season'],
                  ['custom', 'Custom'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={range === key}
                  disabled={key === 'season' && seasonLoading}
                  onClick={() => {
                    if (key === 'season') {
                      void selectSeason();
                      return;
                    }
                    setSeasonError(null);
                    setRange(key);
                  }}
                  className={`inline-flex min-h-9 items-center gap-1.5 rounded-[10px] px-3 text-xs font-medium disabled:cursor-wait disabled:opacity-60 ${focus} ${
                    range === key ? chipOn : chipOff
                  }`}
                >
                  {key === 'season' && seasonLoading ? 'Loading season…' : label}
                </button>
              ))}
            </div>
            {range === 'custom' ? (
              <div className="mt-2 flex gap-2">
                <label className="block flex-1">
                  <span className={`mb-1 block text-xs font-medium ${ap.label}`}>From</span>
                  <input
                    type="date"
                    value={custom.start}
                    onChange={(e) => setCustom(onStartDateChange(e.target.value, custom.end, custom.start))}
                    className={ap.input}
                  />
                </label>
                <label className="block flex-1">
                  <span className={`mb-1 block text-xs font-medium ${ap.label}`}>To</span>
                  <input
                    type="date"
                    value={custom.end}
                    onChange={(e) => setCustom(onEndDateChange(custom.start, e.target.value, custom.end))}
                    className={ap.input}
                  />
                </label>
              </div>
            ) : (
              <p className={`mt-1 text-xs ${seasonError ? (isDark ? 'text-amber-300' : 'text-amber-800') : ap.muted}`}>
                {seasonError ||
                  `${pretty(dates.start)} – ${pretty(dates.end)}${range === 'season' ? ' (this crop season)' : ''}`}
              </p>
            )}
          </>
        )}

        {step(health ? 4 : 2, 'Language')}
        <div className="flex flex-wrap gap-2">
          {(
            [
              ['en', 'English'],
              ['fil', 'Filipino'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={briefLang === value}
              onClick={() => onBriefLang(value)}
              className={`inline-flex min-h-9 items-center rounded-[10px] px-3 text-xs font-medium ${focus} ${
                briefLang === value ? chipOn : chipOff
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {step(health ? 5 : 3, 'How do you want it?')}
        <div className={`mb-3 flex rounded-lg p-0.5 ${ap.segmentWrap}`} role="tablist">
          {(
            [
              ['dl', 'Download now', 'ri-download-line'],
              ['em', 'Email me', 'ri-mail-line'],
            ] as const
          ).map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={delivery === key}
              onClick={() => setDelivery(key)}
              className={`inline-flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-md text-xs font-medium ${focus} ${
                delivery === key ? `${ap.segmentActive} report-on-fill` : ap.segmentInactive
              }`}
            >
              <i className={icon} aria-hidden />
              {label}
            </button>
          ))}
        </div>

        {delivery === 'dl' ? (
          <p className={`text-xs ${ap.muted}`}>You get a PDF on your device. Nothing is sent.</p>
        ) : !canSchedule ? (
          <p className={`text-xs ${ap.muted}`}>Save the field first to schedule email.</p>
        ) : (
          <>
            <span className={`mb-1 mt-2.5 block text-xs font-medium ${ap.label}`}>Send it</span>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['weekly', 'Every Monday'],
                  ['monthly', '1st of the month'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={frequency === key}
                  onClick={() => setFrequency(key)}
                  className={`inline-flex min-h-9 items-center rounded-[10px] px-3 text-xs font-medium ${focus} ${
                    frequency === key ? chipOn : chipOff
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <label className={`mb-1 mt-2.5 block text-xs font-medium ${ap.label}`} htmlFor="report-email">
              Send to
            </label>
            <input
              id="report-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={ap.input}
            />
            <div className="mt-1.5 flex min-h-11 items-center justify-between gap-3">
              <span className="font-semibold">Send on this schedule</span>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label="Send on this schedule"
                onClick={() => setEnabled((v) => !v)}
                className={`relative h-7 w-12 shrink-0 rounded-full ${focus} ${
                  enabled ? (isDark ? 'bg-[#008540]' : 'bg-[#008540]') : isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'
                }`}
              >
                <span
                  className={`absolute top-[3px] h-[22px] w-[22px] rounded-full bg-white transition-[left] duration-150 ${
                    enabled ? 'left-[23px]' : 'left-[3px]'
                  }`}
                />
              </button>
            </div>
            <div className={`mt-2.5 flex gap-2 rounded-[10px] border px-3 py-2.5 text-xs leading-snug ${ap.card} ${ap.body}`}>
              <i className={`ri-mail-line mt-0.5 shrink-0 ${ap.accentText}`} aria-hidden />
              <span>
                {enabled
                  ? `${freqLabel}, 7:00 AM Philippines time.`
                  : 'Schedule is off. Use Send now for a one-time email.'}
                <br />
                {health
                  ? 'The email is a short note on how the crop looks now. Download for the full date range.'
                  : 'Same PDF as the download.'}
              </span>
            </div>
            {subscriptionId && (
              <button
                type="button"
                onClick={() => void remove()}
                disabled={busy !== null}
                className={`mt-2 text-xs font-medium underline-offset-2 hover:underline disabled:opacity-50 ${ap.muted} ${focus}`}
              >
                {busy === 'remove' ? 'Removing…' : 'Remove schedule'}
              </button>
            )}
          </>
        )}
      </div>

      <div className={`shrink-0 border-t px-3 py-3 ${ap.headerBorder}`}>
        {delivery === 'dl' ? (
          <button
            type="button"
            disabled={isGenerating || needsMeasure || needsSeasonData || seasonLoading}
            onClick={() => onDownload(dates)}
            className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-[10px] text-sm font-medium disabled:cursor-not-allowed ${
              isGenerating || needsMeasure || needsSeasonData || seasonLoading
                ? ap.btnDisabled
                : `${ap.primaryBtn} report-on-fill`
            } ${focus}`}
          >
            <i className="ri-file-pdf-line" aria-hidden />
            {isGenerating ? 'Preparing your PDF…' : 'Download PDF'}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!canSchedule || !emailOk || busy !== null}
              onClick={() => void sendNow()}
              className={`inline-flex min-h-11 items-center justify-center gap-1.5 rounded-[10px] border px-3 text-xs font-medium disabled:opacity-50 ${ap.mobileBtn} ${focus}`}
            >
              <i className="ri-send-plane-2-line" aria-hidden />
              {busy === 'send' ? 'Sending…' : 'Send now'}
            </button>
            <button
              type="button"
              disabled={!canSchedule || !emailOk || busy !== null}
              onClick={() => void save()}
              className={`inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-[10px] text-sm font-medium disabled:opacity-50 ${
                !canSchedule || !emailOk || busy !== null ? ap.btnDisabled : `${ap.primaryBtn} report-on-fill`
              } ${focus}`}
            >
              {busy === 'save' ? 'Saving…' : 'Save schedule'}
            </button>
          </div>
        )}
        <p className={`mt-2 text-center text-xs ${ap.muted}`}>
          {status ||
            seasonError ||
            (needsSeasonData ? SEASON_SAVE_FIRST_MSG : null) ||
            (needsMeasure ? 'Pick at least one measure to continue.' : summary)}
        </p>
      </div>
    </div>
  );
}
